import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Heart, MessageCircle } from "lucide-react";
import StarRating from "@/components/StarRating";
import { formatDistanceToNow } from "date-fns";
import { API, useAuth, authFetch } from "@/App";
import { useState } from "react";
import { toast } from "sonner";

export default function FeedCard({ log, onUpdate }) {
  const { user } = useAuth();
  const [isLiked, setIsLiked] = useState(log.liked_by?.includes(user?.user_id));
  const [likeCount, setLikeCount] = useState(log.liked_by?.length || 0);
  const [isLiking, setIsLiking] = useState(false);

  // Get the best available cover image
  const albumCover = log.album?.cover_xl || log.album?.cover_big || log.album?.cover_medium || log.album?.cover_url || log.album?.cover_small || "";
  const albumLink = log.album?.album_id ? `/album/${log.album.album_id}` : "#";

  const handleLike = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isLiking) return;
    setIsLiking(true);

    try {
      const response = await authFetch(`${API}/logs/${log.log_id}/like`, {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        setIsLiked(data.liked);
        setLikeCount(data.like_count);
      }
    } catch (error) {
      toast.error("Failed to like");
    } finally {
      setIsLiking(false);
    }
  };

  const timeAgo = log.listened_at 
    ? formatDistanceToNow(new Date(log.listened_at), { addSuffix: true })
    : "";

  return (
    <div 
      className="glass-card rounded-xl p-4 sm:p-6 card-hover"
      data-testid={`feed-card-${log.log_id}`}
    >
      {/* User header */}
      <div className="flex items-center gap-3 mb-4">
        <Link to={`/profile/${log.user?.user_id}`}>
          <Avatar className="h-10 w-10 border border-white/10">
            <AvatarImage src={log.user?.picture} alt={log.user?.name} />
            <AvatarFallback className="bg-purple-500/20 text-purple-400">
              {log.user?.name?.charAt(0)?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </Link>
        <div className="flex-1 min-w-0">
          <Link 
            to={`/profile/${log.user?.user_id}`}
            className="font-medium text-white hover:text-purple-400 transition-colors"
          >
            {log.user?.name}
          </Link>
          <p className="text-xs text-white/50">{timeAgo}</p>
        </div>
      </div>

      {/* Album info */}
      <Link to={albumLink} className="flex gap-4 group">
        <img
          src={albumCover}
          alt={log.album?.title || "Album"}
          className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-cover flex-shrink-0 group-hover:opacity-90 transition-opacity bg-white/5"
          onError={(e) => { e.target.src = "https://placehold.co/96x96/1a1a2e/9333ea?text=?"; }}
        />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white group-hover:text-purple-400 transition-colors truncate">
            {log.album?.title || "Unknown Album"}
          </h3>
          <p className="text-sm text-white/60 truncate">
            {log.album?.artist || "Unknown Artist"}
          </p>
          {log.rating && (
            <div className="mt-2">
              <StarRating rating={log.rating} readonly size="small" />
            </div>
          )}
        </div>
      </Link>

      {/* Review text */}
      {log.review && (
        <p className="mt-4 text-sm text-white/80 leading-relaxed line-clamp-3">
          {log.review}
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-6 mt-4 pt-4 border-t border-white/5">
        <button
          onClick={handleLike}
          disabled={isLiking}
          className={`flex items-center gap-2 text-sm transition-colors duration-200 ${
            isLiked ? "text-purple-400" : "text-white/50 hover:text-white"
          }`}
          data-testid="like-btn"
        >
          <Heart className={`w-4 h-4 ${isLiked ? "fill-purple-400" : ""}`} />
          <span>{likeCount}</span>
        </button>
        <Link
          to={albumLink}
          className="flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors duration-200"
        >
          <MessageCircle className="w-4 h-4" />
          <span>{log.comment_count || 0}</span>
        </Link>
      </div>
    </div>
  );
}
